import {
  Dialog,
  DialogContent,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Loading from 'src/components/Loading/Loading'
import BaseDialogHeader from 'src/components/Dialogs/Base/BaseDialogHeader'
import BaseDialogFooter from 'src/components/Dialogs/Base/BaseDialogFooter'
import DiscoveryDialogHeader from 'src/components/Dialogs/DiscoveryAssets/DiscoveryDialogHeader'
import DiscoveryDialogFooter from 'src/components/Dialogs/DiscoveryAssets/DiscoveryDialogFooter'
import styles from './BaseDialog.scss'

interface BaseDialogProps {
  backgroundColor?: string
  children?: JSX.Element
  closeDisabled?: boolean
  elementId?: string
  height?: string
  isFullScreen?: boolean
  isDiscoveryDialog?: boolean
  isLmnDialog?: boolean
  isOpen: boolean
  loading?: boolean
  loadingMessage?: string
  onClose: (event?: object, reason?: string) => void
  onLoadingComplete?: () => void
  removePadding?: boolean
  width?: string | false
}

const BaseDialog = ({
  backgroundColor,
  children,
  closeDisabled,
  elementId,
  height,
  isFullScreen,
  isDiscoveryDialog,
  isLmnDialog,
  isOpen,
  loading,
  loadingMessage,
  onClose,
  onLoadingComplete,
  removePadding,
  width,
}: BaseDialogProps): JSX.Element => {
  const { breakpoints } = useTheme()
  const isSmallViewport = useMediaQuery(breakpoints.down('sm'))
  const getDialogContentPadding = (): string | undefined => {
    if (removePadding) {
      return '0px'
    }
    if (isSmallViewport) {
      return '16px 20px'
    }
    return undefined
  }

  const getMaxWidth = (): string => {
    if (isSmallViewport) return 'unset'
    switch (width) {
      case false:
        return 'unset'
      case undefined:
        // if width prop is not provided, default sika dialog to 'xs'
        return '400px'
      default:
        return width
    }
  }

  return (
    <Dialog
      id={elementId ? `${elementId}Base` : ''}
      fullScreen={isSmallViewport || isFullScreen}
      onClose={onClose}
      disableEnforceFocus
      open={isOpen}
      PaperProps={{
        style: {
          maxWidth: getMaxWidth(),
          backgroundColor: backgroundColor ?? 'white',
        },
      }}
    >
      {isDiscoveryDialog ? (
        <DiscoveryDialogHeader
          closeDisabled={closeDisabled}
          onClose={onClose}
        />
      ) : (
        <BaseDialogHeader
          closeDisabled={closeDisabled}
          isFullScreen={isFullScreen}
          isLmnDialog={isLmnDialog}
          loading={loading}
          onClose={onClose}
        />
      )}
      <DialogContent
        id={elementId}
        className={styles.baseDialogContent}
        sx={{
          padding: getDialogContentPadding(),
          height,
          width: getMaxWidth(),
        }}
      >
        {loading ? (
          <Loading
            loading={loading}
            message={loadingMessage}
            onLoadingComplete={onLoadingComplete}
            isFullScreen={isFullScreen}
          />
        ) : children}
      </DialogContent>
      {isDiscoveryDialog ? (
        <DiscoveryDialogFooter />
      ) : (
        <BaseDialogFooter loading={loading} isFullScreen={isFullScreen} />
      )}
    </Dialog>
  )
}

export {
  BaseDialog,
  BaseDialogProps,
}
